import React from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Row, Col, Input, message, Button, Icon, Modal, Breadcrumb } from 'antd'
import AdminLayout from '../../components/AdminLayout'
import SectionHeader from '../../components/SectionHeader'
import SectionHeaderTemplate from '../../components/SectionHeaderTemplate'
import SectionContent from '../../components/SectionContent'
import Table from './Table'
import {
  fetchItems,
  changeTable,
  searchTable,
  editSearch,
  uploadItem,
  deleteItem,
  createFolder,
} from '../../actions/s3AssetList'
import { LIST_PAGESIZE } from '../../constants/app'

const { Search } = Input
const pageTitle = 'Assets'
const storeKey = 's3AssetList'

class ItemList extends React.Component {
  constructor(props, context) {
    super(props, context)
    this.fileInputRef = React.createRef()
    this.state = {
      folderModalVisible: false,
      folderName: '',
      currentFolder: '',
    }
    this.handleTableOnChange = this.handleTableOnChange.bind(this)
    this.handleSearchOnChange = this.handleSearchOnChange.bind(this)
    this.handleSearchOnClick = this.handleSearchOnClick.bind(this)
    this.handleSearchOnCancel = this.handleSearchOnCancel.bind(this)
    this.handleUpload = this.handleUpload.bind(this)
    this.handleDelete = this.handleDelete.bind(this)
    this.handleFileChange = this.handleFileChange.bind(this)
    this.handleUploadClick = this.handleUploadClick.bind(this)
    this.handleCreateFolder = this.handleCreateFolder.bind(this)
    this.handleFolderModalOk = this.handleFolderModalOk.bind(this)
    this.handleFolderModalCancel = this.handleFolderModalCancel.bind(this)
    this.handleFolderClick = this.handleFolderClick.bind(this)
    this.handleBreadcrumbClick = this.handleBreadcrumbClick.bind(this)
  }

  handleUploadClick() {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click()
    }
  }

  componentDidMount() {
    this.fetchItems()
    const { isSearching } = this.props
    !isSearching && this.props.editSearch({ search: null })
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.isFetchItemsLoading && nextProps.fetchItemsError) {
      message.error(nextProps.fetchItemsError)
    }
    if (this.props.isUploadItemLoading && !nextProps.isUploadItemLoading) {
      if (nextProps.uploadItemError) {
        message.error(nextProps.uploadItemError)
      } else {
        message.success('File uploaded successfully')
        this.fetchItems()
      }
    }
    if (this.props.isDeleteItemLoading && !nextProps.isDeleteItemLoading) {
      if (nextProps.deleteItemError) {
        message.error(nextProps.deleteItemError)
      } else {
        message.success('Deleted successfully')
        this.fetchItems() // Refetch to update the list
      }
    }
    if (this.props.isCreateFolderLoading && !nextProps.isCreateFolderLoading) {
      if (nextProps.createFolderError) {
        message.error(nextProps.createFolderError)
      } else {
        message.success('Folder created successfully')
        this.fetchItems()
      }
    }
  }

  fetchItems(folder = this.state.currentFolder) {
    const {
      pagination = {},
      search,
      isSearching,
    } = this.props
    this.props.fetchItems({
      current: pagination.current,
      pageSize: pagination.pageSize || 50,
      search: (isSearching && search) || null,
      folder,
    })
  }

  handleFolderClick(folderName) {
    const newFolder = this.state.currentFolder 
      ? `${this.state.currentFolder}/${folderName}` 
      : folderName
    this.setState({ currentFolder: newFolder }, () => {
      this.fetchItems(newFolder)
    })
  }

  handleBreadcrumbClick(index) {
    const parts = this.state.currentFolder.split('/').filter(Boolean)
    const newFolder = parts.slice(0, index).join('/')
    this.setState({ currentFolder: newFolder }, () => {
      this.fetchItems(newFolder)
    })
  }

  async handleTableOnChange(pagination, filters, sorter) {
    const { columnKey, field, order } = sorter
    await this.props.changeTable({
      pagination,
      filters,
      sorter: {
        columnKey,
        field,
        order,
      },
    })
    this.fetchItems()
  }

  handleSearchOnChange(e) {
    e.preventDefault()
    const search = e.target.value
    this.props.editSearch({ search })
    if (!search) {
      this.props.searchTable({ isSearching: false })
    }
  }

  async handleSearchOnClick() {
    await this.props.searchTable({ isSearching: true })
    await this.props.changeTable({})
    this.fetchItems()
  }

  async handleSearchOnCancel(e) {
    e.preventDefault()
    await this.props.editSearch({ search: null })
    await this.props.searchTable({ isSearching: false })
    await this.props.changeTable({})
    this.fetchItems()
  }

  async handleUpload(data) {
    const { file } = data
    const { currentFolder } = this.state
    try {
      await this.props.uploadItem(file, currentFolder)
      data.onSuccess()
    } catch (error) {
      data.onError(error)
    }
  }

  async handleDelete(record) {
    if (!record || !record.name) {
      message.error('Invalid item')
      return
    }
    const { currentFolder } = this.state
    // Build full path: folder/filename or folder/subfolder/
    let key = currentFolder ? `${currentFolder}/${record.name}` : record.name
    // For folders, append trailing slash
    if (record.isFolder) {
      key += '/'
    }
    await this.props.deleteItem(key)
  }

  handleFileChange(e) {
    const file = e.target.files[0]
    if (file) {
      this.handleUpload({ file, onSuccess: () => {}, onError: (err) => { message.error(err.message) } })
    }
    e.target.value = '' // Reset to allow same file selection
  }

  handleCreateFolder() {
    this.setState({ folderModalVisible: true, folderName: '' })
  }

  async handleFolderModalOk() {
    const { folderName, currentFolder } = this.state
    if (!folderName.trim()) {
      message.error('Please enter a folder name')
      return
    }
    try {
      await this.props.createFolder(folderName.trim(), currentFolder)
      this.setState({ folderModalVisible: false, folderName: '' })
    } catch (error) {
      // Error handled in componentWillReceiveProps
    }
  }

  handleFolderModalCancel() {
    this.setState({ folderModalVisible: false, folderName: '' })
  }

  render() {
    return (
      <div>
        <AdminLayout>
          <SectionHeader>
            <SectionHeaderTemplate
              breadcrumbRoutes={[{ path: '/admin', title: 'Home' }, { title: pageTitle }]}
              title={pageTitle}
            />
          </SectionHeader>
          <SectionContent borderless>
            <Row type='flex' justify='space-between' style={{ marginBottom: '24px' }}>
              <Col>
                <input
                  type='file'
                  ref={this.fileInputRef}
                  style={{ 
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0
                  }}
                  onChange={this.handleFileChange}
                />
                <Button
                  type='primary'
                  loading={this.props.isUploadItemLoading}
                  onClick={this.handleUploadClick}
                  style={{ marginRight: 8 }}
                >
                  <Icon type='upload' /> Upload File
                </Button>
                <Button
                  loading={this.props.isCreateFolderLoading}
                  onClick={this.handleCreateFolder}
                >
                  <Icon type='folder-add' /> Create Folder
                </Button>
              </Col>
              <Col>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Search
                    onChange={this.handleSearchOnChange}
                    onSearch={this.handleSearchOnClick}
                    value={this.props.search}
                    placeholder='Search files...'
                    enterButton
                  />
                  {
                    this.props.isSearching &&
                    <a href='' onClick={this.handleSearchOnCancel} style={{ lineHeight: 1.5, marginLeft: '12px' }}>
                      Cancel
                    </a>
                  }
                </div>
              </Col>
            </Row>
            {/* Breadcrumb Navigation */}
            <div style={{ marginBottom: 16 }}>
              <Breadcrumb>
                <Breadcrumb.Item>
                  <a onClick={() => this.handleBreadcrumbClick(0)}>
                    <Icon type='home' /> Root
                  </a>
                </Breadcrumb.Item>
                {this.state.currentFolder.split('/').filter(Boolean).map((part, index) => (
                  <Breadcrumb.Item key={index}>
                    <a onClick={() => this.handleBreadcrumbClick(index + 1)}>
                      {part}
                    </a>
                  </Breadcrumb.Item>
                ))}
              </Breadcrumb>
            </div>
            <Table
              loading={this.props.isFetchItemsLoading}
              dataSource={this.props.items || []}
              pagination={this.props.pagination}
              filters={this.props.filters}
              sorter={this.props.sorter}
              onChange={this.handleTableOnChange}
              onDelete={this.handleDelete}
              onFolderClick={this.handleFolderClick}
              isDeleteItemLoading={this.props.isDeleteItemLoading}
            />
            <Modal
              title='Create Folder'
              visible={this.state.folderModalVisible}
              onOk={this.handleFolderModalOk}
              onCancel={this.handleFolderModalCancel}
              confirmLoading={this.props.isCreateFolderLoading}
            >
              <Input
                placeholder='Enter folder name'
                value={this.state.folderName}
                onChange={(e) => this.setState({ folderName: e.target.value })}
                onPressEnter={this.handleFolderModalOk}
              />
            </Modal>
          </SectionContent>
        </AdminLayout>
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  const {
    items,
    total,
    isFetchItemsLoading,
    pagination = {},
    filters,
    sorter,
    search,
    isSearching,
    fetchItemsError,
    isUploadItemLoading,
    uploadItemError,
    isDeleteItemLoading,
    deleteItemError,
    isCreateFolderLoading,
    createFolderError,
  } = state[storeKey]
  const editedPagination = {
    ...pagination,
    pageSize: pagination.pageSize || 50,
    pagination: pagination.current || 1,
  }
  return {
    items,
    total,
    isFetchItemsLoading,
    pagination: editedPagination,
    filters,
    sorter,
    search,
    isSearching,
    fetchItemsError,
    isUploadItemLoading,
    uploadItemError,
    isDeleteItemLoading,
    deleteItemError,
    isCreateFolderLoading,
    createFolderError,
  }
}

const mapDispatchToProps = dispatch => ({
  fetchItems: params => dispatch(fetchItems(params)),
  changeTable: params => dispatch(changeTable(params)),
  editSearch: search => dispatch(editSearch(search)),
  searchTable: search => dispatch(searchTable(search)),
  uploadItem: (file, folder) => dispatch(uploadItem(file, folder)),
  deleteItem: key => dispatch(deleteItem(key)),
  createFolder: (folderName, parentFolder) => dispatch(createFolder(folderName, parentFolder)),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ItemList))

